/**
 * Simple Recurrence Field Set - Based on Working Demo
 * 
 * This implementation is based on the flawless demo that works perfectly.
 * Uses simple React state management instead of complex architecture.
 * 
 * Created: 2025-08-17
 * Based on: test-complete-form.html (working demo)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { cn } from '@/utils/common/helpers';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { convertAndSortWeekdays, WEEKDAY_CODE_TO_NAME, convertWeekdayToCode } from '@/utils/events/weekdayConversion';
import DatePicker from '@/components/form/DatePicker';
import { formatDateOnly, parseDateLocal } from '@/utils/formatters/dateUtils';

// Add the slide-down animation styles
const slideDownStyle = {
  animation: 'slideDown 0.3s ease-out'
};

// Add the slide-down keyframes to document head if not already present
if (typeof document !== 'undefined' && !document.querySelector('#recurrence-animations')) {
  const style = document.createElement('style');
  style.id = 'recurrence-animations';
  style.textContent = `
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .slide-down {
      animation: slideDown 0.3s ease-out;
    }
  `;
  document.head.appendChild(style);
}

// Simple interfaces matching V1 for compatibility
export interface SimpleRecurrenceValues {
  isRecurring: boolean;
  recurrencePattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrenceInterval?: number;
  recurrenceByDay?: string[];
  recurrenceEndType?: 'count' | 'until' | 'never';
  recurrenceCount?: number | null;
  recurrenceEndDate?: Date | null;
  startDate: Date;
}

export interface SimpleRecurrenceErrors {
  recurrencePattern?: string;
  recurrenceInterval?: string;
  recurrenceByDay?: string;
  recurrenceEnd?: string;
  recurrenceEndDate?: string;
  weekdays?: string;
}

export interface SimpleRecurrenceProps {
  values: SimpleRecurrenceValues;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: SimpleRecurrenceErrors;
  className?: string;
}

// Exact demo enums and types
const RecurrencePattern = {
  daily: 'daily',
  weekly: 'weekly', 
  monthly: 'monthly',
  yearly: 'yearly'
};

const EndType = {
  never: 'never',
  count: 'count',
  date: 'date'
};

const WeekDay = {
  Sunday: 'SU',
  Monday: 'MO',
  Tuesday: 'TU',
  Wednesday: 'WE', 
  Thursday: 'TH',
  Friday: 'FR',
  Saturday: 'SA'
};

// Simple Switch Component (working from demo)
const Switch: React.FC<{
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onCheckedChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => !disabled && onCheckedChange(!checked)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200
      ${checked 
        ? 'bg-blue-600 dark:bg-blue-500' 
        : 'bg-gray-200 dark:bg-gray-700'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    `}
  >
    <span
      className={`
        inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200
        ${checked ? 'translate-x-6' : 'translate-x-1'}
      `}
    />
  </button>
);

/**
 * Simple, working recurrence field set based on the flawless demo
 */
export const SimpleRecurrenceFieldSet: React.FC<SimpleRecurrenceProps> = ({
  values,
  onChange,
  errors,
  className = ''
}) => {
  // Local state for the UI (like the demo)
  const [isRecurring, setIsRecurring] = useState(values.isRecurring || false);
  const [pattern, setPattern] = useState(values.recurrencePattern || 'weekly');
  const [interval, setInterval] = useState(values.recurrenceInterval || 1);
  const [weekdays, setWeekdays] = useState(values.recurrenceByDay || []);
  const [endType, setEndType] = useState(values.recurrenceEndType || 'never');
  const [endCount, setEndCount] = useState(values.recurrenceCount || 10);
    const [endDate, setEndDate] = useState(values.recurrenceEndDate ? 
    (values.recurrenceEndDate instanceof Date ? formatDateOnly(values.recurrenceEndDate) : values.recurrenceEndDate) : '');
  
  // Local validation errors (internal state)
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Helper to create change events (V1 compatibility) - MOVED UP to fix initialization order
  const createChangeEvent = useCallback((name: string, value: any) => {
    const event = {
      target: { name, value }
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);
  }, [onChange]);

  // Note: convertAndSortWeekdays is now imported from shared utility
  // @see gym-admin-dashboard/src/utils/events/weekdayConversion.ts
 
  // Sync with parent values
  useEffect(() => {
    setIsRecurring(values.isRecurring || false);
    setPattern(values.recurrencePattern || 'weekly');
    setInterval(values.recurrenceInterval || 1);
    setEndType(values.recurrenceEndType || 'never');
    setEndCount(values.recurrenceCount || 10);
    
    // Handle weekdays with auto-selection logic
    if (values.recurrenceByDay && values.recurrenceByDay.length > 0) {
      // Use provided weekdays
      setWeekdays(values.recurrenceByDay);
    } else if (values.isRecurring && (values.recurrencePattern || 'weekly') === 'weekly' && values.startDate) {
      // Auto-select weekday based on start date for weekly pattern when no weekdays are provided
      const startDate = values.startDate instanceof Date ? values.startDate : new Date(values.startDate);
      const dayOfWeek = startDate.getDay(); // 0=Sunday, 1=Monday, etc.
      
      // Map day numbers to weekday codes
      const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      const autoSelectedDay = dayMap[dayOfWeek];
      
      setWeekdays([autoSelectedDay]);
      // Trigger the change event to sync with parent
      setTimeout(() => createChangeEvent('recurrenceByDay', [autoSelectedDay]), 0);
    } else {
      setWeekdays([]);
    }
    
    if (values.recurrenceEndDate) {
      const dateStr = values.recurrenceEndDate instanceof Date ? 
        formatDateOnly(values.recurrenceEndDate) : 
        values.recurrenceEndDate;
      setEndDate(dateStr);
    }
  }, [values, createChangeEvent]);

  // Validation (exact from demo)
  const validateForm = useCallback(() => {
    const newErrors: {[key: string]: string} = {};
    
    // Day selection validation is now handled by the handlers (at least one day must be selected)
    
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, []);

  // Auto-validation (exact from demo)
  useEffect(() => {
    if (isRecurring) {
      validateForm();
    }
  }, [isRecurring, pattern, weekdays, validateForm]);

  // Pattern options (from demo)
  const patternOptions = [
    { value: 'daily', label: 'Daily', icon: 'today' },
    { value: 'weekly', label: 'Weekly', icon: 'calendar_view_week' },
    { value: 'monthly', label: 'Monthly', icon: 'calendar_view_month' },
    { value: 'yearly', label: 'Yearly', icon: 'event_repeat' }
  ];

  // Weekday options (exact from demo)
  const weekdayOptions = [
    { value: WeekDay.Sunday, label: 'Su', full: 'Sunday' },
    { value: WeekDay.Monday, label: 'Mo', full: 'Monday' },
    { value: WeekDay.Tuesday, label: 'Tu', full: 'Tuesday' },
    { value: WeekDay.Wednesday, label: 'We', full: 'Wednesday' },
    { value: WeekDay.Thursday, label: 'Th', full: 'Thursday' },
    { value: WeekDay.Friday, label: 'Fr', full: 'Friday' },
    { value: WeekDay.Saturday, label: 'Sa', full: 'Saturday' }
  ];

  // Handle recurrence toggle
  const handleRecurrenceToggle = useCallback((checked: boolean) => {
    setIsRecurring(checked);
    createChangeEvent('isRecurring', checked);
    
    if (checked) {
      // Set defaults when enabling
      createChangeEvent('recurrencePattern', pattern);
      createChangeEvent('recurrenceInterval', interval);
      createChangeEvent('recurrenceEndType', endType);
      if (endType === 'count') {
        createChangeEvent('recurrenceCount', endCount);
      }
      
      // Auto-select weekday based on start date for weekly pattern
      if (pattern === 'weekly' && values.startDate) {
        const startDate = values.startDate instanceof Date ? values.startDate : new Date(values.startDate);
        const dayOfWeek = startDate.getDay(); // 0=Sunday, 1=Monday, etc.
        
        // Map day numbers to weekday codes
        const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
        const autoSelectedDay = dayMap[dayOfWeek];
        
        // Convert to uppercase abbreviation before saving
        const converted = convertAndSortWeekdays([autoSelectedDay]);
        setWeekdays(converted);
        createChangeEvent('recurrenceByDay', converted);
      }
      
    } else {
      // Clear all recurrence data when disabling
      setPattern('weekly');
      setInterval(1);
      setWeekdays([]);
      setEndType('never');
      setEndCount(10);
      setEndDate('');
      createChangeEvent('recurrenceByDay', []);
      createChangeEvent('recurrenceCount', null);
      createChangeEvent('recurrenceEndDate', null);
    }
  }, [pattern, interval, endType, endCount, values.startDate, createChangeEvent]);

  // Handle pattern change
  const handlePatternChange = useCallback((newPattern: string) => {
    setPattern(newPattern as 'daily' | 'weekly' | 'monthly' | 'yearly');
    createChangeEvent('recurrencePattern', newPattern);
    
    // Handle weekday selection based on pattern
    if (newPattern === 'weekly' && values.startDate) {
      // Auto-select weekday based on start date when switching to weekly
      const startDate = values.startDate instanceof Date ? values.startDate : new Date(values.startDate);
      const dayOfWeek = startDate.getDay(); // 0=Sunday, 1=Monday, etc.
      
      // Map day numbers to weekday codes  
      const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      const autoSelectedDay = dayMap[dayOfWeek];
      
      // Convert to uppercase abbreviation before saving
      const converted = convertAndSortWeekdays([autoSelectedDay]);
      setWeekdays(converted);
      createChangeEvent('recurrenceByDay', converted);
    } else if (newPattern !== 'weekly') {
      // Reset weekly days when changing away from weekly
      setWeekdays([]);
      createChangeEvent('recurrenceByDay', []);
    }
  }, [values.startDate, createChangeEvent]);

  // Handle day toggle (exact from demo)
  const handleDayToggle = useCallback((day: string) => {
    const isSelected = weekdays.includes(day);
    
    // Prevent deselecting the only selected day
    if (isSelected && weekdays.length === 1) return;

    const newValue = isSelected 
      ? weekdays.filter(d => d !== day)
      : [...weekdays, day];
    
    // Convert to uppercase abbreviations before saving
    const converted = convertAndSortWeekdays(newValue);
    setWeekdays(converted);
    createChangeEvent('recurrenceByDay', converted);
  }, [weekdays, createChangeEvent]);

  // Handle quick weekday selection (exact from demo)
  const handleQuickWeekdays = useCallback((type: string) => {
    const weekdayList = [WeekDay.Monday, WeekDay.Tuesday, WeekDay.Wednesday, WeekDay.Thursday, WeekDay.Friday];
    const weekendList = [WeekDay.Saturday, WeekDay.Sunday];
    const allList = [WeekDay.Sunday, WeekDay.Monday, WeekDay.Tuesday, WeekDay.Wednesday, WeekDay.Thursday, WeekDay.Friday, WeekDay.Saturday];
    
    let targetList: string[] = [];
    switch (type) {
      case 'weekdays': targetList = weekdayList; break;
      case 'weekends': targetList = weekendList; break;
      case 'all': targetList = allList; break;
    }

    const allInTargetSelected = targetList.every(day => weekdays.includes(day));
    
    let nextWeekdays: string[];
    if (allInTargetSelected) {
      // Toggle OFF: remove targetList from current selection
      nextWeekdays = weekdays.filter(day => !targetList.includes(day));
      
      // Prevent clearing all days
      if (nextWeekdays.length === 0) return;
    } else {
      // Toggle ON: add targetList to current selection
      nextWeekdays = Array.from(new Set([...weekdays, ...targetList]));
    }
    
    // Convert to uppercase abbreviations before saving
    const converted = convertAndSortWeekdays(nextWeekdays);
    setWeekdays(converted);
    createChangeEvent('recurrenceByDay', converted);
  }, [weekdays, createChangeEvent]);

  // Helper to check if a quick select group is fully active
  const isGroupActive = useCallback((type: string) => {
    if (weekdays.length === 0) return false;
    
    const weekdayList = [WeekDay.Monday, WeekDay.Tuesday, WeekDay.Wednesday, WeekDay.Thursday, WeekDay.Friday];
    const weekendList = [WeekDay.Saturday, WeekDay.Sunday];
    const allList = [WeekDay.Sunday, WeekDay.Monday, WeekDay.Tuesday, WeekDay.Wednesday, WeekDay.Thursday, WeekDay.Friday, WeekDay.Saturday];
    
    let targetList: string[] = [];
    switch (type) {
      case 'weekdays': targetList = weekdayList; break;
      case 'weekends': targetList = weekendList; break;
      case 'all': targetList = allList; break;
    }
    
    return targetList.length > 0 && targetList.every(day => weekdays.includes(day));
  }, [weekdays]);

  // Handle end type change
  const handleEndTypeChange = useCallback((type: string) => {
    setEndType(type as 'never' | 'count' | 'until');
    createChangeEvent('recurrenceEndType', type);
    
    if (type === 'count') {
      createChangeEvent('recurrenceCount', endCount);
      createChangeEvent('recurrenceEndDate', null);
    } else if (type === 'until') {
      createChangeEvent('recurrenceCount', null);
      if (endDate) {
        createChangeEvent('recurrenceEndDate', parseDateLocal(endDate));
      }
    } else {
      createChangeEvent('recurrenceCount', null);
      createChangeEvent('recurrenceEndDate', null);
    }
  }, [endCount, endDate, createChangeEvent]);

  // Get today's date for min date
  const getTodayString = () => {
    const today = new Date();
    return formatDateOnly(today);
  };

  // Generate simple RRULE preview
  const generateRRule = useCallback(() => {
    if (!isRecurring) return '';
    
    let rrule = `FREQ=${pattern.toUpperCase()}`;
    if (interval > 1) rrule += `;INTERVAL=${interval}`;
    
    if (pattern === 'weekly' && weekdays.length > 0) {
      const sortedWeekdays = convertAndSortWeekdays(weekdays);
      rrule += `;BYDAY=${sortedWeekdays.join(',')}`;
    }
    
    if (endType === 'count') rrule += `;COUNT=${endCount}`;
    if (endType === 'until' && endDate) rrule += `;UNTIL=${endDate.replace(/-/g, '')}T235959Z`;
    
    return rrule;
  }, [isRecurring, pattern, interval, weekdays, endType, endCount, endDate]);

  return (
    <div className={cn('space-y-6', className)}>
      {/* Recurrence Toggle - Mobile-optimized stacking */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className={`
            flex items-center justify-center rounded-lg p-2 transition-colors duration-200 shrink-0
            ${isRecurring 
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' 
              : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
            }
          `}>
            <GoogleMaterialIcon 
              name={isRecurring ? "repeat" : "event"} 
            />
          </div>
          <div className="flex-1 min-w-0">
            <label className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
              Enable Recurrence
            </label>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create a series of repeating events
            </p>
          </div>
        </div>

        <div className="flex justify-end sm:justify-start">
          <Switch
            checked={isRecurring}
            onCheckedChange={handleRecurrenceToggle}
          />
        </div>
      </div>

      {/* Status Indicator */}
      {isRecurring && (
        <div className="flex items-center space-x-2 text-sm font-medium text-blue-600 dark:text-blue-400 slide-down">
          <GoogleMaterialIcon name="check_circle" size="xs" />
          <span>Recurrence enabled</span>
        </div>
      )}

      {/* Recurrence Configuration */}
      {isRecurring && (
        <fieldset className="space-y-6 pl-4 border-l-2 border-blue-200 dark:border-blue-800 slide-down">
          <legend className="sr-only">Recurrence Configuration</legend>
          
          {/* Pattern Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              How often should this repeat?
            </label>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {patternOptions.map((option) => {
                const isSelected = pattern === option.value;
                
                return (
                  <label
                    key={option.value}
                    className={cn(
                      'relative flex items-center justify-center rounded-lg border-2 cursor-pointer transition-all duration-200 group p-4',
                      isSelected 
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300 transform hover:-translate-y-1 hover:shadow-lg' 
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:-translate-y-1 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    )}
                  >
                    <input
                      type="radio"
                      name="pattern"
                      value={option.value}
                      checked={isSelected}
                      onChange={() => handlePatternChange(option.value)}
                      className="sr-only"
                    />
                    
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className={`
                        flex items-center justify-center rounded-lg p-2 transition-colors duration-200
                        ${isSelected 
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' 
                          : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-500 dark:bg-gray-800 dark:text-gray-500 dark:group-hover:bg-gray-700'
                        }
                      `}>
                        <GoogleMaterialIcon name={option.icon}  />
                      </div>
                      <div className="font-medium">{option.label}</div>
                    </div>

                    {isSelected && (
                      <div className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-blue-500 text-white rounded-full">
                        <GoogleMaterialIcon name="check" size="xs" />
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Interval Input - Mobile-friendly wrap */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Repeat every
            </label>
            <div className="w-16 sm:w-20">
              <input
                type="number"
                value={interval}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  if (value >= 1 && value <= 999) {
                    setInterval(value);
                    createChangeEvent('recurrenceInterval', value);
                  }
                }}
                min={1}
                max={999}
                className="w-full px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
              />
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {pattern === 'daily' ? 'day' : 
               pattern === 'weekly' ? 'week' :
               pattern === 'monthly' ? 'month' : 'year'}{interval !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Weekly Days Selection - ENHANCED CARDS */}
          {pattern === RecurrencePattern.weekly && (
            <div className="space-y-4 slide-down">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Repeat on
              </label>

              {/* Weekday Circles - Forced single row on mobile with smaller sizes */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2 max-w-sm mx-auto">
                {weekdayOptions.map((option) => {
                  const isSelected = weekdays.includes(option.value);
                  
                  return (
                    <label
                      key={option.value}
                      className={cn(
                        'relative flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 rounded-full border-2 cursor-pointer transition-all duration-200 mx-auto',
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300 transform hover:-translate-y-0.5' 
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                      )}
                      title={option.full}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleDayToggle(option.value)}
                        className="sr-only"
                      />
                      
                      <span className="font-bold text-[10px] sm:text-sm">{option.label}</span>

                      {isSelected && (
                        <div className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4 bg-blue-500 text-white rounded-full border border-white dark:border-gray-900">
                          <GoogleMaterialIcon name="check" size="xs" style={{ fontSize: '8px' }} className="sm:!text-[10px]" />
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Quick Select Cards - Matching Pattern Style */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'weekdays', label: 'Weekdays' },
                  { id: 'weekends', label: 'Weekends' },
                  { id: 'all', label: 'All Days' }
                ].map((type) => {
                  const isSelected = isGroupActive(type.id);
                  
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleQuickWeekdays(type.id)}
                      className={cn(
                        'relative flex items-center justify-center rounded-lg border-2 transition-all duration-200 p-2 text-center',
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300 transform hover:-translate-y-0.5' 
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                      )}
                    >
                      <span className="font-medium text-xs sm:text-sm">{type.label}</span>
                      
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 bg-blue-500 text-white rounded-full">
                          <GoogleMaterialIcon name="check" size="xs" style={{ fontSize: '10px' }} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Selection Summary */}
              {weekdays.length > 0 && (
                <div className="flex items-center justify-center space-x-2 text-sm text-blue-600 dark:text-blue-400">
                  <span className="material-symbols-outlined text-sm">check_circle</span>
                  <span>
                    {weekdays.length === 7 
                      ? 'Every day'
                      : weekdays.length === 5 && weekdays.every(day => [WeekDay.Monday, WeekDay.Tuesday, WeekDay.Wednesday, WeekDay.Thursday, WeekDay.Friday].includes(day))
                        ? 'Weekdays'
                        : weekdays.length === 2 && weekdays.every(day => [WeekDay.Saturday, WeekDay.Sunday].includes(day))
                          ? 'Weekends'
                          : `${weekdays.length} day${weekdays.length === 1 ? '' : 's'} selected`
                    }
                  </span>
                </div>
              )}
            </div>
          )}

          {/* End Condition */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              When should this end?
            </label>

            <div className="space-y-3">
              {/* Never - Mobile-optimized layout */}
              <div
                className={cn(
                  'rounded-lg border-2 p-3 sm:p-4 cursor-pointer transition-all duration-200',
                  endType === 'never'
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950' 
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                )}
                onClick={() => handleEndTypeChange('never')}
              >
                <div className="flex items-center space-x-3">
                  <div className={`
                    w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 shrink-0
                    ${endType === 'never' 
                      ? 'border-blue-500 bg-blue-500' 
                      : 'border-gray-300 dark:border-gray-600'
                    }
                  `}>
                    {endType === 'never' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${endType === 'never' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                      Never
                    </div>
                    <div className={`text-sm ${endType === 'never' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      Continues indefinitely
                    </div>
                  </div>
                </div>
              </div>

              {/* After Count - Mobile-optimized layout */}
              <div
                className={cn(
                  'rounded-lg border-2 p-3 sm:p-4 cursor-pointer transition-all duration-200',
                  endType === 'count'
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950' 
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                )}
                onClick={() => handleEndTypeChange('count')}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <div className="flex items-center space-x-3">
                    <div className={`
                      w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 shrink-0
                      ${endType === 'count' 
                        ? 'border-blue-500 bg-blue-500' 
                        : 'border-gray-300 dark:border-gray-600'
                      }
                    `}>
                      {endType === 'count' && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium ${endType === 'count' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                        After
                      </div>
                      <div className={`text-sm ${endType === 'count' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        Stop after a number of events
                      </div>
                    </div>
                  </div>
                  {endType === 'count' && (
                    <div className="flex items-center space-x-2 pl-7 sm:pl-0 sm:ml-auto" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="number"
                        value={endCount}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setEndCount(value);
                          createChangeEvent('recurrenceCount', value);
                        }}
                        min="1"
                        max="999"
                        className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        event{endCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* On Date - Mobile-optimized layout */}
              <div
                className={cn(
                  'rounded-lg border-2 p-3 sm:p-4 cursor-pointer transition-all duration-200',
                  endType === 'until'
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950' 
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                )}
                onClick={() => handleEndTypeChange('until')}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <div className="flex items-center space-x-3">
                    <div className={`
                      w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 shrink-0
                      ${endType === 'until' 
                        ? 'border-blue-500 bg-blue-500' 
                        : 'border-gray-300 dark:border-gray-600'
                      }
                    `}>
                      {endType === 'until' && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium ${endType === 'until' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                        On Date
                      </div>
                      <div className={`text-sm ${endType === 'until' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        Stop on a specific date
                      </div>
                    </div>
                  </div>
                  {endType === 'until' && (
                    <div className="flex items-center pl-7 sm:pl-0 sm:ml-auto" onClick={(e) => e.stopPropagation()}>
                      <DatePicker
                        label=""
                        id="recurrenceEndDate"
                        defaultDate={endDate ? parseDateLocal(endDate) : new Date()}
                        onChange={(selectedDates) => {
                          if (selectedDates && selectedDates.length > 0) {
                            const date = selectedDates[0];
                            const dateStr = formatDateOnly(date);
                            setEndDate(dateStr);
                            createChangeEvent('recurrenceEndDate', date);
                          } else {
                            setEndDate('');
                            createChangeEvent('recurrenceEndDate', null);
                          }
                        }}
                        placeholder="Select end date"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      )}

      {/* Live Preview - Mobile-optimized with wrapping */}
      {isRecurring && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4">
          <div className="flex items-center space-x-2 mb-2 sm:mb-3">
            <GoogleMaterialIcon name="preview" className="text-blue-600 dark:text-blue-400" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">Live Preview</h4>
          </div>
          
          <div className="space-y-2 text-xs sm:text-sm">
            <div className="text-gray-700 dark:text-gray-300">
              <span className="font-medium block sm:inline mb-1 sm:mb-0">RRULE:</span> 
              <code className="sm:ml-2 px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs block sm:inline-block overflow-x-auto max-w-full">
                {generateRRule() || 'Invalid configuration'}
              </code>
            </div>
            {pattern === 'weekly' && weekdays.length > 0 && (
              <div className="text-gray-600 dark:text-gray-400">
                <span className="font-medium">Days:</span> {(() => {
                  const sortedWeekdays = convertAndSortWeekdays(weekdays);
                  return sortedWeekdays.map(day => WEEKDAY_CODE_TO_NAME[day] || day).join(', ');
                })()}
              </div>
            )}
          </div>
        </div>
      )}

             {/* Form Status with helpful error messages */}
       <div className="space-y-2" aria-live="polite" aria-atomic="true">
         {/* Show specific error messages */}
         {(Object.keys(validationErrors).length > 0 || Object.keys(errors).length > 0) && (
           <div className="space-y-1">
             {/* Internal validation errors */}
             {Object.entries(validationErrors).map(([key, message]) => (
               <div key={key} className="flex items-center space-x-1 text-red-600 dark:text-red-400 text-sm">
                 <span className="material-symbols-outlined text-sm">error</span>
                 <span>{message}</span>
               </div>
             ))}
             
             {/* Parent component errors */}
             {Object.entries(errors).map(([key, message]) => (
               message && (
                 <div key={key} className="flex items-center space-x-1 text-red-600 dark:text-red-400 text-sm">
                   <span className="material-symbols-outlined text-sm">error</span>
                   <span>{message}</span>
                 </div>
               )
             ))}
           </div>
         )}
         
         {/* Success message when valid */}
         {Object.keys(validationErrors).length === 0 && Object.keys(errors).length === 0 && (
           <div className="flex items-center space-x-1 text-green-600 dark:text-green-400 text-sm">
             <span className="material-symbols-outlined text-sm">check_circle</span>
             <span>Valid configuration</span>
           </div>
         )}
       </div>
    </div>
  );
};

export default SimpleRecurrenceFieldSet;
